import { DataGrid } from '@mui/x-data-grid';
import { get } from 'lodash';
import { GridCellModes } from '@mui/x-data-grid-pro';
import i18next from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';

import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';
import WhiteCard from '@commons/WhiteCard';

import { getNutritionFactsColumns, nutritionFactsHeaders } from './getColumns';
import { NutritionFactsContainer } from './styledComponents';

const NutritionFacts = (props) => {
  const { nutritionFacts, setNutritionFacts, warnOfMissingConversion } = props;

  const [columns, setColumns] = useState([]);
  const [nutritionFactRows, setNutritionFactRows] = useState([]);

  const [cellModesModel, setCellModesModel] = useState({});

  useEffect(() => {
    setColumns(getNutritionFactsColumns());

    const initialNutritionFacts = nutritionFactsHeaders().map(
      ({ value, label, isSubNutritionFact, tooltipText }) => ({
        id: value,
        label,
        value: get(nutritionFacts, value),
        isSubNutritionFact,
        tooltipText,
      }),
    );

    setNutritionFactRows(initialNutritionFacts);
  }, [nutritionFacts]);

  /**
   * Allow focus in a single click on cell in MUI DataGrid
   * {@link} https://github.com/mui/mui-x/issues/2186
   */
  const handleCellClick = useCallback((params, event) => {
    if (!params.isEditable) {
      return;
    }

    // Ignore portal
    if (event.target.nodeType === 1 && !event.currentTarget.contains(event.target)) {
      return;
    }

    setCellModesModel((prevModel) => ({
      // Revert the mode of the other cells from other rows
      ...Object.entries(prevModel).reduce(
        (acc, [id, value]) => ({
          ...acc,
          [id]: Object.keys(value).reduce(
            (acc2, field) => ({
              ...acc2,
              [field]: { mode: GridCellModes.View },
            }),
            {},
          ),
        }),
        {},
      ),
      [params.id]: {
        // Revert the mode of other cells in the same row
        ...Object.keys(prevModel[params.id] || {}).reduce(
          (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
          {},
        ),
        [params.field]: { mode: GridCellModes.Edit },
      },
    }));
  }, []);

  const handleProcessRowUpdate = (params) => {
    const newValue = params.value.replace(',', '.');

    // Prevent negative values and non-numeric values
    if (isNaN(newValue) || newValue < 0) {
      params.value = null;

      return params;
    }

    const updatedNutritionFactRows = nutritionFactRows.map((row) =>
      row.id === params.id ? { ...row, value: newValue || null } : row,
    );

    const updatedNutritionFacts = updatedNutritionFactRows.reduce((acc, row) => {
      acc[row.id] = row.value;
      return acc;
    }, {});

    setNutritionFacts(updatedNutritionFacts);
    setNutritionFactRows(updatedNutritionFactRows);
  };

  return (
    <WhiteCard
      customStyle={{ maxWidth: '816px', width: '816px' }}
      renderContent={
        <NutritionFactsContainer>
          {warnOfMissingConversion && (
            <Text
              color={ENUM_COLORS.INFO_ORANGE}
              font={ENUM_FONTS.TEXT_SMALL_HEIGHT_12}
              style={{ marginBottom: '16px' }}
            >
              {i18next.t(
                `ADMIN.SUPPLIER_PRODUCTS.MISSING_CONVERSION_WARNING_${warnOfMissingConversion}`,
              )}
            </Text>
          )}
          <DataGrid
            cellModesModel={cellModesModel}
            columns={columns}
            processRowUpdate={handleProcessRowUpdate}
            rows={nutritionFactRows}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                display: 'none',
              },
              // Prevent green border around non editable cells
              '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within':
                {
                  outline: 'none',
                },
              '& .MuiDataGrid-cell': {
                borderRight: '1px solid rgba(224, 224, 224, 1)',
              },
              '& .MuiDataGrid-cell:last-child': {
                borderRight: 'none',
              },
            }}
            autoHeight
            disableColumnFilter
            disableColumnMenu
            disableColumnResize
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            hideFooter
            ignoreDiacritics
            onCellClick={handleCellClick}
            onCellEditStop={(params) => {
              // Allow immediate row update after blur event
              if (params.reason === 'cellFocusOut') {
                setCellModesModel((prev) => ({
                  ...prev,
                  [params.id]: {
                    ...prev[params.id],
                    [params.field]: { mode: GridCellModes.View },
                  },
                }));
              }
            }}
            onCellKeyDown={(params, event) => {
              // Allow going to the next row on pressing enter
              if (event.key === 'Enter') {
                event.preventDefault();

                const currentRowIndex = nutritionFactRows.findIndex((row) => row.id === params.id);
                const nextRow = nutritionFactRows[currentRowIndex + 1];

                if (!nextRow) {
                  return;
                }

                setCellModesModel((prevModel) => ({
                  ...prevModel,
                  [params.id]: {
                    ...prevModel[params.id],
                    [params.field]: { mode: GridCellModes.View },
                  },
                  [nextRow.id]: {
                    [params.field]: { mode: GridCellModes.Edit },
                  },
                }));
              }
            }}
          />
        </NutritionFactsContainer>
      }
      title={i18next.t('ADMIN.SUPPLIER_PRODUCTS.NUTRITION_FACTS_TITLE')}
    />
  );
};

export default NutritionFacts;
